import { useCallback } from 'react';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import authService from '@services/auth';
import { useAppDispatch } from '@redux/hooks';
import { setUserData } from '@redux/reducers/user.reducer';
import { setTokenLocalStorage, setTokenSessionStorage } from '@config/storage';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '@hooks/useTranslation';

export type ResetFirstTimeParams = {
  email: string;
  newPassword: string;
  confirmToken: string;
  rememberMe: boolean;
};

export type UseResetPasswordFirstTimeResult = {
  resetFirstTimeMutation: UseMutationResult<
    boolean,
    Error,
    ResetFirstTimeParams
  >;
  isLoading: boolean;
  isSuccess: boolean;
};

export const useResetPasswordFirstTime =
  (): UseResetPasswordFirstTimeResult => {
    const tActionMessages = useTranslation('actionMessages');
    const dispatch = useAppDispatch();
    const history = useHistory();

    const handleTokenLogin = useCallback(
      async (token: string, rememberMe: boolean) => {
        dispatch(setUserData(token, rememberMe));
        setTokenLocalStorage(token);
        setTokenSessionStorage(token);

        const from = sessionStorage.getItem('redirectUrl') || '/';
        sessionStorage.removeItem('redirectUrl');
        if (from && from !== '/logout') {
          history.push(from);
        }
      },
      [dispatch, history],
    );

    const resetFirstTimeMutation = useMutation({
      mutationFn: async (params: ResetFirstTimeParams) => {
        const data = await authService.signInWithConfirm(
          params.email,
          params.newPassword,
          params.confirmToken,
        );
        if (data?.token) {
          await handleTokenLogin(data.token, params.rememberMe);
          toast.success(tActionMessages.passwordSetSuccessfully);
          return;
        }
      },
      onSuccess: (data: any) => {
        if (data?.confirmation === '200') {
          toast.success(tActionMessages.passwordSetSuccessfully);
          return;
        }
      },
      onError: (e: any) => {
        const errorMessage =
          e?.response?.data?.errorMessage || e?.message || tActionMessages.requestError;
        toast.error(errorMessage);
      },
    });

    return {
      resetFirstTimeMutation: resetFirstTimeMutation as any,
      isLoading: resetFirstTimeMutation.isPending,
      isSuccess: resetFirstTimeMutation.isSuccess,
    };
  };

export default useResetPasswordFirstTime;
